<script setup lang="ts">
import { DownloadGraphKey } from "@/modules/explorer/components/Graphs/store";
import { inject, onMounted, onUnmounted, ref, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import Switch from "@/ui/components/switch/switch.vue";
import useToast from "@/ui/composables/useToast";

const isDialogVisible = defineModel<boolean>();

const { downloadKey, chartTitle } = defineProps<{
	downloadKey: DownloadGraphKey;
	chartTitle: string;
}>();

const imageAspectRatio = ref<number>(null);
const imageURL = ref<string>(null);

const downloadGraphState = inject(downloadKey);
const downloadProperties = downloadGraphState.properties;

const resolutionPresets = [
	{
		title: "Keep Aspect Ratio",
		value: null,
	},
	{
		title: "16:9",
		value: { w: 960, h: 540 },
	},
	{
		title: "4:3",
		value: { w: 800, h: 600 },
	},
	{
		title: "1:1",
		value: { w: 1000, h: 1000 },
	},
];

const backgroundColors = [
	{
		title: "White",
		value: "white",
	},
	{
		title: "Transparent",
		value: "transparent",
	},
];

const cleanup = () => {
	if (imageURL.value) {
		URL.revokeObjectURL(imageURL.value);
	}

	downloadGraphState.graphImage.value = null;
	downloadGraphState.startDownload.value = false;
	isDialogVisible.value = false;
};

const downloadGraphAsImage = () => {
	if (!downloadGraphState.graphImage.value) {
		return;
	}
	const link = document.createElement("a");
	link.href = imageURL.value;
	link.download = "graph.png";
	link.click();

	cleanup();
};

const copyToClipboardAsImage = async () => {
	if (!downloadGraphState.graphImage.value) {
		return;
	}
	await navigator.clipboard.write([
		new ClipboardItem({
			"image/png": downloadGraphState.graphImage.value,
		}),
	]);

	useToast().success("Image copied to clipboard", {
		timeout: 2000,
	});
};

watch(
	() => downloadGraphState.graphImage.value,
	async (blob) => {
		if (!blob) return;

		imageURL.value = URL.createObjectURL(blob);
		const bitmap = await createImageBitmap(blob);
		imageAspectRatio.value = bitmap.width / bitmap.height;
		bitmap.close();
	}
);

onMounted(() => {
	downloadGraphState.graphImage.value = null;
	downloadGraphState.startDownload.value = true;
	downloadGraphState.graphTitle.value = chartTitle.replace(/<[^>]*>/g, "");
});

onUnmounted(cleanup);
</script>

<template>
	<Modal
		title="Download Chart Image"
		icon="fa-solid fa-image"
		v-model:visible="isDialogVisible"
		width="800px"
	>
		<v-row>
			<v-col cols="4">
				<v-select
					v-model="downloadProperties.resolution"
					label="Aspect Ratio"
					:items="resolutionPresets"
					variant="filled"
					density="comfortable"
					hide-details="auto"
					class="mb-3"
				/>
				<v-select
					v-model="downloadProperties.backgroundColor"
					label="Background Color"
					:items="backgroundColors"
					variant="filled"
					density="comfortable"
					hide-details="auto"
					class="mb-3"
				/>
				<div class="d-flex flex-column ga-1">
					<Switch
						v-model="downloadProperties.showTitle"
						label="Show title"
						label-position="right"
					/>
					<Switch
						v-model="downloadProperties.showSnapshotDate"
						label="Show snapshot date"
						label-position="right"
					/>
					<Switch
						v-model="downloadProperties.showChartConfig"
						label="Show chart configuration"
						label-position="right"
					/>
				</div>
			</v-col>

			<v-col cols="8">
				<v-card
					variant="outlined"
					color="secondary"
					:style="{
						aspectRatio: imageAspectRatio ? imageAspectRatio : 'unset',
					}"
				>
					<img
						v-if="downloadGraphState.graphImage.value"
						:src="imageURL"
						alt="Graph"
						style="width: 100%; height: 100%; object-fit: contain"
					/>
					<div
						v-else
						class="d-flex justify-center align-center"
						style="height: 100%"
					>
						<v-progress-circular indeterminate color="primary" />
					</div>
				</v-card>
			</v-col>
		</v-row>
		<template #actions>
			<v-btn color="red" @click="isDialogVisible = false"> Cancel</v-btn>
			<v-btn
				@click="downloadGraphAsImage"
				color="secondary"
				height="40"
				variant="flat"
				prepend-icon="mdi-download"
			>
				Download
			</v-btn>
			<v-btn
				@click="copyToClipboardAsImage"
				color="primary"
				height="40"
				variant="flat"
				prepend-icon="mdi-clipboard-outline"
			>
				Copy To Clipboard
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss"></style>
